import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChange,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { TreatmentDesignQCChecklistItem } from './model';
import { TranslateValuePipe } from '../../core/pipe/translate-value.pipe';
import { SplitAndTranslatePipe } from '../../core/pipe/split-and-translate.pipe';
import { RichTextComponent } from '../rich-text/rich-text.component';

@Component({
    selector: 'app-qc-checklist',
    standalone: true,
    imports: [CommonModule, CheckboxModule, FormsModule, TranslateValuePipe, SplitAndTranslatePipe, RichTextComponent],
    providers: [TranslateValuePipe],
    templateUrl: './qc-checklist.component.html',
    styleUrls: ['./qc-checklist.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QCChecklistComponent implements OnChanges {
    @Input() items: TreatmentDesignQCChecklistItem[] = [];
    @Input() readonly = false;
    @Input() showHeader = true;

    @Output() readonly itemsChange = new EventEmitter<TreatmentDesignQCChecklistItem[]>();
    @Output() readonly allConfirmedChange = new EventEmitter<boolean>();
    @Output() readonly itemConfirmedChange = new EventEmitter<TreatmentDesignQCChecklistItem>();

    allConfirmed = false;

    ngOnChanges(changes: Record<keyof QCChecklistComponent, SimpleChange>): void {
        if (changes.items) {
            this.updateAllConfirmedState();
        }
    }

    onItemConfirmedChange(item: TreatmentDesignQCChecklistItem): void {
        if (item.isConfirmed) {
            item.confirmedAt = new Date();
        } else {
            item.confirmedAt = null;
            item.confirmedBy = null;
        }

        this.itemConfirmedChange.emit(item);
        this.itemsChange.emit(this.items);
        this.updateAllConfirmedState();
    }

    onItemClick(item: TreatmentDesignQCChecklistItem): void {
        if (this.readonly) {
            return;
        }

        item.isConfirmed = !item.isConfirmed;
        this.onItemConfirmedChange(item);
    }

    toggleAll(confirmed: boolean): void {
        const now = new Date();
        this.items.forEach(item => {
            item.isConfirmed = confirmed;
            if (confirmed) {
                item.confirmedAt = now;
            } else {
                item.confirmedAt = null;
                item.confirmedBy = null;
            }
        });

        this.itemsChange.emit(this.items);
        this.updateAllConfirmedState();
    }

    getUnconfirmedItems(): TreatmentDesignQCChecklistItem[] {
        return this.items.filter(item => !item.isConfirmed);
    }

    private updateAllConfirmedState(): void {
        const previousState = this.allConfirmed;
        this.allConfirmed = this.items.length > 0 && this.items.every(item => item.isConfirmed);

        if (previousState !== this.allConfirmed) {
            this.allConfirmedChange.emit(this.allConfirmed);
        }
    }
}
